/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { MouseEvent as ReactMouseEvent } from 'react';

import { labelStyles } from '@zapier/style-encapsulation';
/* eslint-disable import/no-duplicates */
import { Animation, Icon } from '@zapier/design-system';
import { Tooltip } from '@zapier/design-system-beta';
import type { IconName } from '@zapier/design-system';

import { SIDEBAR_ID } from '../UniversalSidebar/constants';

type Props = {
  /**
   * ARIA attribute indicating whether or not the content
   * controlled by the button is visible.
   */
  ariaExpanded?: boolean;
  /**
   * ARIA attribute to label the button if `label` is empty.
   */
  ariaLabel?: string;
  /**
   * Name of the `Icon` to render.
   */
  icon?: IconName;
  /**
   *  Indicates what the button does. Used for the `ariaLabel` on the
   * `Button` as well as the text for the `Tooltip`.
   * This prop should be supplied in most circumstances, but in cases
   * **when it's not desired be sure to pass `ariaLabel`!**
   */
  label?: string;
  /**
   * Callback to be executed when the button is clicked.
   */
  onClick?: (e: ReactMouseEvent) => void;
};

const Styles = labelStyles('MenuCollapseButton', {
  root: css`
    background: none;
    border: none;
    width: var(--zds-size-36, 36px);
    height: var(--zds-size-36, 36px);
    border-radius: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--zds-gray-warm-6, #88827e);
    transition: ${Animation.transitionValue};

    &:hover,
    &:focus {
      background-color: none;
      color: var(--zds-gray-warm-10, #201515);
    }
  `,
});

/**
 * @private
 * `MenuCollapseButton` renders button to toggle whether the sidebar is open or closed.
 */
const MenuCollapseButton = ({
  ariaExpanded = false,
  ariaLabel = undefined,
  icon = 'navMenu',
  label = 'Menu',
  onClick = undefined,
}: Props) => {
  return (
    <Tooltip content={label} placement="right">
      <Tooltip.FocusableTrigger
        aria-controls={SIDEBAR_ID}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel || label}
        css={Styles.root}
        onClick={(event: ReactMouseEvent) => {
          if (onClick) {
            onClick(event);
          }
        }}
        tag="button"
      >
        <Icon name={icon} size={24} />
      </Tooltip.FocusableTrigger>
    </Tooltip>
  );
};

export default MenuCollapseButton;
